import { Backdrop, Badge, Button, Dot, ThemeToggle } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../state/auth';
import {
  IconCalendar,
  IconCards,
  IconCheck,
  IconChart,
  IconChat,
  IconClose,
  IconHelp,
  IconLogout,
  IconMenu,
  IconNotebook,
  IconStar,
  IconTarget,
  IconUser,
} from './Icons';
import { Logo } from './Logo';

interface NavItem {
  to: string;
  label: string;
  Icon: (props: { className?: string }) => ReactNode;
}

const NAV: NavItem[] = [
  { to: '/baralhos', label: 'Flashcards', Icon: IconCards },
  { to: '/desempenho', label: 'Desempenho', Icon: IconChart },
  { to: '/cronograma', label: 'Cronograma', Icon: IconCalendar },
  { to: '/simulado', label: 'Simulado', Icon: IconTarget },
  { to: '/caderno', label: 'Caderno', Icon: IconNotebook },
  { to: '/resumos', label: 'Resumos & Mapas', Icon: IconNotebook },
  { to: '/questoes', label: 'Questões', Icon: IconCheck },
  { to: '/favoritas', label: 'Favoritas', Icon: IconStar },
];

const NAV_MOVEL: NavItem[] = NAV.flatMap((i) =>
  i.to === '/favoritas'
    ? [
        { ...i, label: 'Favoritas dos flashcards' },
        { to: '/questoes?favoritas=1', label: 'Favoritas das questões', Icon: IconStar },
      ]
    : [i],
);

export function AppShell(): ReactNode {
  const { user, sair } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAberto, setMenuAberto] = useState(false);

  // Sem isto o menu do telemóvel ficava aberto por cima da página nova.
  useEffect(() => setMenuAberto(false), [location.pathname]);

  const diasRestantes = user?.acesso.diasRestantes ?? null;
  const avisoExpiracao =
    diasRestantes !== null && diasRestantes <= 7 && user?.role === 'user';

  return (
    <div className="relative min-h-screen">
      <Backdrop />

      <a
        href="#conteudo"
        className="sr-only rounded-mc-pill bg-mc-accent px-4 py-2 text-mc-deep focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
      >
        Pular para o conteúdo
      </a>

      <header className="mc-nav sticky top-0 z-40 border-b border-mc-line bg-mc-surface-nav backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-mc-content items-center gap-4 px-4 sm:px-6">
          <NavLink to="/baralhos" className="shrink-0" aria-label="Início">
            <Logo />
          </NavLink>

          <nav className="ml-2 hidden min-w-0 flex-1 items-center gap-1 xl:flex" aria-label="Principal">
            <NavItemLink item={NAV[0]!} />
            <NavItemLink item={NAV[1]!} />
            <NavItemLink item={NAV[6]!} />
            <GrupoMenu
              rotulo="Estudar"
              Icon={IconCalendar}
              itens={[NAV[2]!, NAV[3]!, NAV[4]!, NAV[5]!].map((i) => ({ to: i.to, label: i.label }))}
            />
            <GrupoMenu
              rotulo="Favoritas"
              Icon={IconStar}
              itens={[
                { to: '/favoritas', label: 'Favoritas dos flashcards' },
                { to: '/questoes?favoritas=1', label: 'Favoritas das questões' },
              ]}
            />
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ThemeToggle size="sm" className="hidden sm:grid" />
            <NavLink
              to="/ajuda"
              title="Central de ajuda"
              className={({ isActive }) =>
                `hidden size-9 place-items-center rounded-mc-pill border transition-colors sm:grid ${
                  isActive
                    ? 'border-mc-accent/60 text-mc-accent'
                    : 'border-mc-line2 text-mc-ink2 hover:text-mc-ink'
                }`
              }
              aria-label="Central de ajuda"
            >
              <IconHelp className="size-4" />
            </NavLink>
            <NavLink
              to="/feedback"
              title="Feedback"
              className={({ isActive }) =>
                `hidden size-9 place-items-center rounded-mc-pill border transition-colors sm:grid ${
                  isActive
                    ? 'border-mc-accent/60 text-mc-accent'
                    : 'border-mc-line2 text-mc-ink2 hover:text-mc-ink'
                }`
              }
              aria-label="Feedback"
            >
              <IconChat className="size-4" />
            </NavLink>
            {avisoExpiracao ? (
              <Badge tone="warning" className="hidden sm:inline-flex">
                {diasRestantes === 0
                  ? 'Vence hoje'
                  : `${diasRestantes} ${diasRestantes === 1 ? 'dia' : 'dias'}`}
              </Badge>
            ) : null}

            <NavLink
              to="/conta"
              className={({ isActive }) =>
                `hidden items-center gap-2 rounded-mc-pill border px-3 py-2 text-sm transition-colors sm:inline-flex ${
                  isActive
                    ? 'border-mc-accent/60 text-mc-accent'
                    : 'border-mc-line2 text-mc-ink2 hover:text-mc-ink'
                }`
              }
            >
              <IconUser className="size-4" />
              <span className="max-w-[10rem] truncate">
                {user?.nome || user?.email.split('@')[0]}
              </span>
            </NavLink>

            {user?.role === 'superadmin' ? (
              <NavLink
                to="/admin"
                className="hidden rounded-mc-pill border border-mc-accent/30 px-3 py-2 text-xs font-medium text-mc-accent sm:inline-flex"
              >
                Admin
              </NavLink>
            ) : null}

            <Button
              variant="quiet"
              size="sm"
              onClick={() => {
                void sair().then(() => navigate('/entrar'));
              }}
              className="hidden sm:inline-flex"
            >
              <IconLogout className="size-4" />
              <span className="sr-only">Sair</span>
            </Button>

            <ThemeToggle size="sm" className="sm:hidden" />

            <button
              type="button"
              className="grid size-11 place-items-center rounded-mc-pill border border-mc-line2 text-mc-ink xl:hidden"
              aria-expanded={menuAberto}
              aria-controls="menu-movel"
              aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
              onClick={() => setMenuAberto((v) => !v)}
            >
              {menuAberto ? <IconClose className="size-5" /> : <IconMenu className="size-5" />}
            </button>
          </div>
        </div>

        {menuAberto ? (
          <nav
            id="menu-movel"
            className="border-t border-mc-line bg-mc-surface-nav-solid px-4 pb-4 pt-2 xl:hidden"
            aria-label="Principal"
          >
            <ul className="flex flex-col gap-1">
              {[
                ...NAV_MOVEL,
                { to: '/ajuda', label: 'Central de ajuda', Icon: IconHelp },
                { to: '/feedback', label: 'Feedback', Icon: IconChat },
                { to: '/conta', label: 'Minha conta', Icon: IconUser },
              ].map(
                (item) => (
                  <li key={item.to}>
                    <NavItemLink item={item} bloco />
                  </li>
                ),
              )}
              <li>
                <button
                  type="button"
                  onClick={() => {
                    void sair().then(() => navigate('/entrar'));
                  }}
                  className="flex min-h-[44px] w-full items-center gap-3 rounded-mc-field px-3 text-left text-mc-ink2 hover:bg-mc-surface-hover"
                >
                  <IconLogout className="size-5" />
                  Sair
                </button>
              </li>
            </ul>
          </nav>
        ) : null}
      </header>

      {avisoExpiracao ? (
        <div className="border-b border-mc-warning/30 bg-mc-warning/10">
          <div className="mx-auto flex max-w-mc-content flex-wrap items-center gap-3 px-4 py-2.5 text-sm text-mc-ink2 sm:px-6">
            <Dot className="bg-mc-warning shadow-none" />
            <span>
              {diasRestantes === 0
                ? 'Sua assinatura vence hoje.'
                : `Sua assinatura vence em ${diasRestantes} ${
                    diasRestantes === 1 ? 'dia' : 'dias'
                  }.`}
            </span>
            <NavLink
              to="/conta"
              className="font-medium text-mc-accent underline underline-offset-4"
            >
              Renovar agora
            </NavLink>
          </div>
        </div>
      ) : null}

      <main id="conteudo" className="relative z-[2]">
        <Outlet />
      </main>

      <footer className="relative z-[2] border-t border-mc-line px-4 py-8 text-center sm:px-6">
        <p className="mc-mono">
          MemorizeCards ·{' '}
          <NavLink to="/ajuda" className="text-mc-accent underline underline-offset-2">
            Central de ajuda
          </NavLink>{' '}
          · Suporte: contato@memorizecards.com.br
        </p>
      </footer>
    </div>
  );
}

function NavItemLink({ item, bloco = false }: { item: NavItem; bloco?: boolean }): ReactNode {
  const { Icon } = item;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        [
          'flex items-center gap-2 rounded-mc-pill transition-colors',
          bloco
            ? 'min-h-[44px] w-full px-3'
            : 'min-h-[40px] whitespace-nowrap px-2.5 text-sm 2xl:px-4',
          isActive
            ? 'bg-mc-accent/12 text-mc-accent ring-1 ring-mc-accent/40'
            : 'text-mc-ink2 hover:bg-mc-surface-hover hover:text-mc-ink',
        ].join(' ')
      }
    >
      <Icon className={bloco ? 'size-5' : 'hidden size-4 2xl:block'} />
      {item.label}
    </NavLink>
  );
}

/** Grupo de itens num dropdown (mantém o menu compacto em telas pequenas). */
function GrupoMenu({
  rotulo,
  Icon,
  itens,
}: {
  rotulo: string;
  Icon: NavItem['Icon'];
  itens: { to: string; label: string }[];
}): ReactNode {
  const [aberto, setAberto] = useState(false);
  const location = useLocation();
  const atual = location.pathname + location.search;
  const ativo = itens.some((i) => atual === i.to || (!i.to.includes('?') && location.pathname.startsWith(i.to)));
  useEffect(() => setAberto(false), [location.pathname, location.search]);
  useEffect(() => {
    if (!aberto) return;
    const fechar = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    window.addEventListener('keydown', fechar);
    return () => window.removeEventListener('keydown', fechar);
  }, [aberto]);
  const sub = 'block whitespace-nowrap rounded-mc-field px-3 py-2 text-sm text-mc-ink2 hover:bg-mc-surface-hover hover:text-mc-ink';
  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
        className={[
          'flex min-h-[40px] items-center gap-2 whitespace-nowrap rounded-mc-pill px-2.5 text-sm transition-colors 2xl:px-4',
          ativo
            ? 'bg-mc-accent/12 text-mc-accent ring-1 ring-mc-accent/40'
            : 'text-mc-ink2 hover:bg-mc-surface-hover hover:text-mc-ink',
        ].join(' ')}
      >
        <Icon className="hidden size-4 2xl:block" />
        {rotulo}
        <span aria-hidden className="text-xs">▾</span>
      </button>
      {aberto ? (
        <>
          <button
            type="button"
            aria-label="Fechar submenu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setAberto(false)}
          />
          <div
            role="menu"
            className="absolute left-0 top-full z-50 mt-2 min-w-[200px] rounded-mc-field border border-mc-line bg-mc-surface-nav-solid p-1 shadow-lg"
          >
            {itens.map((i) => (
              <NavLink key={i.to} role="menuitem" to={i.to} className={sub}>
                {i.label}
              </NavLink>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
